import { useState, useEffect, useMemo } from 'react'
import { ThemeType } from '../types'
import { themeMap, ChangelogTheme } from '../themes/themes'

interface UseThemeReturn {
  appliedTheme: 'light' | 'dark'
  themeClass: string
}

// Function to apply theme CSS custom properties to the document
const applyThemeToDocument = (
  theme: ChangelogTheme,
  containerElement?: HTMLElement,
) => {
  const targetElement = containerElement || document.documentElement

  Object.entries(theme).forEach(([property, value]) => {
    targetElement.style.setProperty(property, value)
  })
}

// Function to apply theme to a specific container element
const applyThemeToContainer = (
  containerElement: HTMLElement,
  themeName: 'light' | 'dark',
) => {
  const theme = themeMap[themeName]
  Object.entries(theme).forEach(([property, value]) => {
    containerElement.style.setProperty(property, value)
  })
}

export const useTheme = (theme: ThemeType): UseThemeReturn => {
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>('light')
  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (theme === 'auto') {
      // Check system preference
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      setSystemTheme(mediaQuery.matches ? 'dark' : 'light')

      // Listen for changes
      const handleChange = (e: MediaQueryListEvent) => {
        setSystemTheme(e.matches ? 'dark' : 'light')
      }

      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
  }, [theme])

  const appliedTheme = useMemo(() => {
    if (theme === 'auto') {
      return systemTheme
    }
    return theme
  }, [theme, systemTheme])

  const themeClass = useMemo(() => {
    return `changelog-theme-${appliedTheme}`
  }, [appliedTheme])

  // Apply theme CSS custom properties when theme changes
  useEffect(() => {
    const themeConfig = themeMap[appliedTheme]
    applyThemeToDocument(themeConfig)

    // Find and update changelog containers
    const containers = document.querySelectorAll('.react-changelog-container')
    containers.forEach((container) => {
      if (container instanceof HTMLElement) {
        applyThemeToContainer(container, appliedTheme)
      }
    })
  }, [appliedTheme])

  return {
    appliedTheme,
    themeClass,
  }
}

// Export utility functions for manual theme application
export { applyThemeToDocument, applyThemeToContainer, themeMap }
